/**
 * UNA TARJETA DEL MURO: un partido contado como novedad.
 *
 * NO ES EL DIBUJO DEL CUADRO. En el cuadro un partido es una llave —dos
 * nombres y una raya— porque lo que se lee ahí es la forma del torneo. Acá lo
 * que se lee es qué pasó: quién le ganó a quién, con cuánto, en qué torneo, y
 * qué dijo la gente. Son dos preguntas distintas y por eso son dos dibujos.
 *
 * LO QUE SE PUEDE HACER LO DICE EL SERVIDOR, en `canScore` y en `canDelete`.
 * Esta pantalla no vuelve a preguntarse «¿soy administrador?»: esa regla vive
 * en un solo lugar, y una segunda copia dice algo distinto el día que cambie.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { MatchComment, WallEntry } from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { CommentThread, Reactions } from './Conversation';
import { nombreDeRonda } from './NextMatches';
import { assetUrl } from '../lib/api';
import { categoryName } from '../lib/categoryName';
import { formatDay } from '../lib/formatDay';

export function WallCard({
  entrada,
  yoId,
  onScore,
  onComment,
  onDeleteComment,
  onReact,
  editor,
}: {
  entrada: WallEntry;
  /** Para poder decir «este soy yo» sin volver a preguntarle al servidor. */
  yoId: string;
  onScore: (entrada: WallEntry) => void;
  onComment: (entrada: WallEntry, body: string) => Promise<void>;
  onDeleteComment: (comentario: MatchComment) => Promise<void>;
  onReact: (entrada: WallEntry, emoji: string, on: boolean) => Promise<void>;
  /** El editor de marcador, cuando esta tarjeta es la que se está anotando. */
  editor?: JSX.Element | null;
}): JSX.Element {
  const { t } = useTranslation();
  const { match } = entrada;

  const ganador = match.home?.id === match.winnerEntryId ? match.home : match.away;
  const perdedor = match.home?.id === match.winnerEntryId ? match.away : match.home;
  const jugado = match.winnerEntryId !== null;

  return (
    <article className="wall-card">
      <header className="wall-head">
        <span className="wall-round">
          {nombreDeRonda(t, entrada.roundSize, 1)}
          {' · '}
          {categoryName(t, match)}
        </span>
        <Link className="wall-club" to={`/torneos/${entrada.tournamentId}`}>
          {entrada.tournamentName}
        </Link>
        <span className="help wall-where">{entrada.communityName}</span>
      </header>

      {/*
        EL «vs 3» SOLO CUANDO YO SOY UNO DE LOS DOS.
        La cuenta sale de MI historial; entre dos terceros el servidor manda
        cero y acá no se dibuja nada. Recorrer las rivalidades de gente ajena
        no es una pregunta que esta aplicación tenga que contestar.
      */}
      {entrada.playedTogether > 0 && (
        <p className="wall-vs">
          <Link
            className="tag tag-vs"
            to={`/jugadores/${match.home?.userId === yoId ? match.away?.userId : match.home?.userId}/vs`}
          >
            {t('challenges.playedTogether', { count: entrada.playedTogether })}
          </Link>
        </p>
      )}

      {/*
        Y EL CARA A CARA DE ELLOS DOS CUANDO NO JUEGO YO.
        Javier: «deberia poder ver el cara a cara de ambos jugadores». Va sin
        número porque contar cada par de la página serían decenas de consultas
        por scroll; la cuenta completa la hace la pantalla del cara a cara. Los
        dos tienen que tener cuenta: contra un invitado no hay qué consultar.
      */}
      {entrada.playedTogether === 0 &&
        match.home?.userId != null &&
        match.away?.userId != null &&
        match.home.userId !== yoId &&
        match.away.userId !== yoId && (
          <p className="wall-vs">
            <Link
              className="tag tag-vs"
              to={`/jugadores/${match.home.userId}/vs/${match.away.userId}`}
            >
              {t('rivalry.between')}
            </Link>
          </p>
        )}

      <div className="wall-players">
        <Lado entry={match.home} gano={jugado && match.home?.id === match.winnerEntryId} jugado={jugado} />
        <span className="wall-vs-word" aria-hidden="true">
          vs
        </span>
        <Lado entry={match.away} gano={jugado && match.away?.id === match.winnerEntryId} jugado={jugado} />
      </div>

      {/* LA FRASE Y EL MARCADOR, que es lo que la gente viene a leer. Sin
          jugar todavía, lo que hay que decir es cuándo y dónde. */}
      {jugado ? (
        <>
          <p className="wall-line">
            {t('wall.beats', {
              winner: ganador?.displayName ?? '',
              loser: perdedor?.displayName ?? '',
            })}
          </p>
          <p className="wall-score">
            {match.score.length === 0
              ? t('score.wo')
              : match.score.map((set) => `${set.home}-${set.away}`).join(', ')}
          </p>
        </>
      ) : (
        <p className="help wall-when">{cuandoYDonde(match) ?? t('bracket.noDateYet')}</p>
      )}

      {!jugado && entrada.deadline !== null && (
        <p className="help wall-deadline">{t('wall.deadline', { date: formatDay(entrada.deadline) })}</p>
      )}

      {/* ANOTAR: el mismo permiso del cuadro, el mismo editor. */}
      {match.canScore === true && (
        <p className="wall-score-action">
          <button type="button" className="link" onClick={() => onScore(entrada)}>
            {t('score.addOf', {
              home: match.home?.displayName ?? '',
              away: match.away?.displayName ?? '',
            })}
          </button>
        </p>
      )}
      {editor}

      <Reactions
        reactions={entrada.reactions}
        onToggle={(emoji, on) => void onReact(entrada, emoji, on)}
      />
      <CommentThread
        id={entrada.match.id}
        comments={entrada.comments}
        onComment={(body) => onComment(entrada, body)}
        onDelete={onDeleteComment}
      />
    </article>
  );
}

function Lado({
  entry,
  gano,
  jugado,
}: {
  entry: WallEntry['match']['home'];
  gano: boolean;
  jugado: boolean;
}): JSX.Element {
  if (entry === null) return <span className="wall-side" />;

  /*
   * UN INVITADO NO TIENE FICHA QUE ABRIR.
   *
   * No tiene cuenta, así que `/jugadores/null` es una pantalla de error con
   * el nombre de una persona real encima. Se dibuja igual —jugó el partido,
   * su nombre y su resultado son parte de lo que pasó— pero sin enlace.
   */
  const dentro = (
    <>
      <Avatar url={assetUrl(entry.avatarPath)} name={entry.displayName} size={44} />
      <span className="wall-name">{entry.displayName}</span>
      {/* La marca solo cuando ya se jugó: sin resultado, un ✕ al lado de
          alguien se lee como que perdió. */}
      {jugado && (
        <span className={gano ? 'wall-mark wall-mark-won' : 'wall-mark wall-mark-lost'} aria-hidden="true">
          {gano ? '✓' : '✕'}
        </span>
      )}
    </>
  );

  const clase = gano ? 'wall-side wall-won' : 'wall-side';
  return entry.userId === null ? (
    <span className={clase}>{dentro}</span>
  ) : (
    <Link className={clase} to={`/jugadores/${entry.userId}`}>
      {dentro}
    </Link>
  );
}

/** Cuándo y dónde, en una línea. `null` si no se sabe nada. */
function cuandoYDonde(match: WallEntry['match']): string | null {
  const partes = [
    match.schedule.date === null ? null : formatDay(match.schedule.date),
    match.schedule.time,
    match.schedule.place,
    match.schedule.court,
  ].filter((parte): parte is string => parte !== null && parte !== '');
  return partes.length === 0 ? null : partes.join(' · ');
}
